import { PaymentElement } from '@stripe/react-stripe-js';
import ReactModal from 'react-modal';
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Layout } from "@layouts";
import { Card } from "@molecules";
import { Button, Icon, Spinner } from "@atoms";
import { ActionBanner } from "@molecules/action-banner";
import { BillingPageTemplateProps } from "./";
import { styles as globalStyles } from "../../../styles/styles";
import { subscriptionPlanData } from "./subscription-data";
import { SubscriptionPlansTypes } from "../../../types";

export const BillingPageTemplate = ({
  modalIsOpen,
  closeModal,
  onPressAddCard,
  onCardUpdated,
  onPressDownloadInvoice,
  selectedCardIndex,
  cardDataList,
  creditCardStatus,
  invoices,
  handleSubmit,
  succeeded,
  processing,
  disabled,
  error,
  isAddressSubmitted,
  handleUpgradeClick,
  handleSendEmailClick,
  planAmount
}: BillingPageTemplateProps): JSX.Element => {
  const { t } = useTranslation();
  const [isPaymentElementReady, setPaymentElementReady] = useState(false);

  useEffect(() => {
    if (modalIsOpen) {
      setPaymentElementReady(false);
    }
  }, [modalIsOpen]);

  // const stripeCardStyle = {
  //   style: {
  //     base: {
  //       color: "#32325d",
  //       fontFamily: 'Arial, sans-serif',
  //       fontSmoothing: "antialiased",
  //       fontSize: "16px",
  //       "::placeholder": {
  //         color: "#32325d"
  //       }
  //     },
  //     invalid: {
  //       fontFamily: 'Arial, sans-serif',
  //       color: "#fa755a",
  //       iconColor: "#fa755a"
  //     }
  //   }
  // };


  // TODO: refactor this page since it seems very similar to onboarding-add-card (this page doesn't have the Continue button or Invoices, but is quite similar)


  const isButtonDisabled = processing || disabled || succeeded || !isPaymentElementReady;
  const billingCta = () => onPressAddCard();

  return (
    <Layout
      variant="dashboard"
      header={t("nav.subscription")}
      subheader={
        <div className="page-subtitle mt-2">
          {t("billing.subheader")}
        </div>
      }
    >
      <div className="w-full max-w-5xl px-0 py-4 space-y-[18px]">
        <div className="space-y-[18px]">
          {!isAddressSubmitted && (
            <div className="mt-2 max-w-2xl rounded-xl bg-attention-lightest px-6 py-3 text-attention-darkest">
              {t("billing.addressAlert", { organizationLabel: t("nav.organization") })}
            </div>
          )}
          {creditCardStatus === "expired" && (
            <ActionBanner
              text="🚨 Your credit card has expired, please update your info."
              onClick={billingCta}
            />
          )}
          {creditCardStatus === "expiringSoon" && (
            <ActionBanner
              text="⚠️ Your credit card is expiring soon, please update your info."
              onClick={billingCta}
            />
          )}
          <div className="flex flex-col gap-[18px] lg:flex-row lg:items-start lg:gap-[18px]">
            <div className="flex-1 min-w-0 space-y-[18px] pr-4">
              <Card
                variant="paymentMethod"
                title={t("billing.cardsTitle")}
                buttonText={t("billing.addCard")}
                icon={<Icon variant="creditCard" size={25}/>}
                iconSubtitle={t("billing.noCards")}
                onClick={isAddressSubmitted ? onPressAddCard : undefined}
                hasTransactionFee={false}
                hasAddButton={!(Array.isArray(cardDataList) && cardDataList.length > 0)}
                data={cardDataList}
                selectedIndex={selectedCardIndex}
              />
              <Card
                variant="invoices"
                title={t("billing.invoicesTitle")}
                icon={<div></div>}
                iconSubtitle={t("billing.noInvoices")}
                onClick={onPressDownloadInvoice}
                hasAddButton={false}
                data={invoices}
              />
            </div>
            <div className="w-full max-w-xs">
              <Card
                variant="subscription"
                planType={SubscriptionPlansTypes.basic}
                planAmount={planAmount}
                subscriptionPlanData={subscriptionPlanData[SubscriptionPlansTypes.basic]}
                // todo: add the plan pricing as a variable
                onClick={() => handleUpgradeClick()}
                onSendEmail={() => handleSendEmailClick()}
              />
            </div>
          </div>
        </div>
        {modalIsOpen ? (
          <ReactModal
            isOpen={modalIsOpen}
            onRequestClose={closeModal}
            style={globalStyles.generalModal}
            shouldCloseOnOverlayClick={true}
            ariaHideApp={false}
          >
            <div className="font-semibold text-2xl flex flex-row justify-center pt-2">{t("billing.modalTitle")}</div>
            <form className="px-2" id="payment-form" onSubmit={handleSubmit}>
              <div className="flex flex-col justify-center items-center">
                <div
                  className="relative w-full"
                  style={{ minHeight: 180, padding: "15px 0" }}
                >
                  {!isPaymentElementReady && (
                    <div
                      className="absolute inset-0 flex items-center justify-center bg-white/60"
                      style={{ padding: "15px 0" }}
                    >
                      <Spinner variant="large" />
                    </div>
                  )}
                  <PaymentElement onReady={() => setPaymentElementReady(true)} />
                </div>
                <div className="mt-6 w-56">
                  <Button
                    onClick={isButtonDisabled ? undefined : handleSubmit}
                    variant="primary"
                    label={processing ? t("billing.registering") : t("billing.registerCard")}
                    textWeight={"semibold"}
                    color="primary-medium"
                    stretch
                  />
                </div>
                {/* Show any error that happens when processing the card add */}
                {error && (
                  <div className="card-error" role="alert">
                    {error}
                  </div>
                )}
                {/* Show a success message upon completion */}
                <p className={succeeded ? "result-message" : "result-message hidden"}>
                  {t("billing.cardAdded")}
                </p>
              </div>
            </form>
          </ReactModal>
        ) : null}
      </div>
    </Layout>
  );
};
